import type { ProgrammaticLandingPageConfig } from '../../types'
import { KWP_SIZE_FACTS } from '../kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[12]

export const KWP_12_INSTALACJA_FOTOWOLTAICZNA: ProgrammaticLandingPageConfig = {
  slug: 'instalacja-fotowoltaiczna-12-kwp-cena',
  lang: 'pl',
  topicId: 'kwp-kosten-12',
  title: 'Instalacja fotowoltaiczna 12 kWp: cena i wartości orientacyjne',
  metaDescription:
    'Ile kosztuje instalacja fotowoltaiczna 12 kWp? Powierzchnia dachu, liczba paneli, ' +
    'rozbicie kosztów i bezpłatne porównanie cen dla domów z pompą ciepła.',
  keywords: [
    'instalacja fotowoltaiczna 12 kWp cena',
    'panele słoneczne 12 kWp koszt',
    'ile kosztuje instalacja 12 kWp',
    '12 kWp instalacja fotowoltaiczna pompa ciepła',
  ],
  eyebrow: 'Wielkość instalacji: 12 kWp',
  h1: 'Instalacja fotowoltaiczna 12 kWp: cena i wartości orientacyjne',
  intro:
    'Instalacja 12 kWp odpowiednia jest dla większych domów jednorodzinnych, często już z pompą ' +
    'ciepła. Znajdziesz tu wartości orientacyjne dotyczące powierzchni dachu i liczby paneli, ' +
    'rozbicie kosztów, a tuż poniżej kalkulator z już wypełnioną średnią ceną dla 12 kWp, obliczoną ' +
    'na podstawie prawdziwych ofert firm rzemieślniczych.',
  calculatorPrefill: { kind: 'kwp', kwp: 12 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Dla kogo odpowiednia jest instalacja 12 kWp?' },
    {
      type: 'paragraph',
      text:
        'Wartość orientacyjna: 12 kWp zwykle pasuje do większego domu jednorodzinnego z pompą ' +
        'ciepła jako dodatkowym odbiornikiem prądu, całkowite roczne zużycie często w przedziale ' +
        '7 000-9 000 kWh. Bez pompy ciepła większość gospodarstw pokrywa już mniejsza wielkość, np. ' +
        '8 lub 10 kWp.',
    },
    { type: 'heading', text: 'Dach i panele: czego potrzebuje instalacja 12 kWp' },
    {
      type: 'paragraph',
      text:
        `Wartość orientacyjna: ok. ${FACTS.roofAreaM2} m² powierzchni dachu i ok. ` +
        `${FACTS.moduleCount} paneli przy typowych modułach ~440 Wp. Roczna produkcja przy ` +
        `orientacji południowej bez zacienienia wynosi ok. ${FACTS.annualYieldKwh.toLocaleString('pl-PL')} kWh. ` +
        'Dokładne obliczenie dla Twojego dachu daje bezpłatny kalkulator doboru instalacji.',
    },
    { type: 'heading', text: 'Składniki kosztów instalacji fotowoltaicznej' },
    {
      type: 'list',
      items: [
        'Panele: największa pojedyncza pozycja kosztowa, zwykle około jednej trzeciej całości',
        'Falownik: przy 12 kWp często potrzebny jest już model trójfazowy',
        'Montaż/robocizna: mocowanie na dachu, okablowanie',
        'Rusztowanie: zależnie od wysokości i dostępności dachu',
        'Instalacja elektryczna: dostosowanie rozdzielnicy, często z myślą o pompie ciepła',
        'Zgłoszenie: rejestracja u operatora sieci i w odpowiednich rejestrach',
      ],
    },
    {
      type: 'paragraph',
      text:
        'Od ok. 12 kWp warto dokładniej sprawdzić przyłącze elektryczne domu - w zależności od ' +
        'posiadanego zabezpieczenia może być potrzebny falownik trójfazowy, co czyni instalację ' +
        'elektryczną nieco bardziej złożoną niż przy mniejszych instalacjach.',
    },
    { type: 'heading', text: 'Amortyzacja i autokonsumpcja' },
    {
      type: 'paragraph',
      text:
        'Z pompą ciepła jako dodatkowym odbiornikiem, wytworzoną energię często można dobrze ' +
        'wykorzystać na własne potrzeby nawet bez dużego magazynu energii, zwłaszcza gdy pompa ' +
        'ciepła pracuje głównie w ciągu dnia. Dokładne obliczenie dla Twojego dachu daje bezpłatny ' +
        'kalkulator doboru instalacji.',
    },
  ],
  faq: [
    {
      q: 'Czy 12 kWp wystarczy dla pompy ciepła i samochodu elektrycznego razem?',
      a: 'Zależy od dokładnego zużycia obu urządzeń - przy oszczędnej pompie ciepła i umiarkowanym użytkowaniu samochodu elektrycznego często tak, przy wysokim łącznym zużyciu bezpieczniejszym wyborem jest 15 kWp.',
    },
    {
      q: 'Ile minimum powierzchni dachu potrzebuję do 12 kWp?',
      a: `Wartość orientacyjna: ok. ${FACTS.roofAreaM2} m² użytecznej powierzchni dachu, w zależności od wybranego typu paneli i orientacji.`,
    },
    {
      q: 'Czy przy 12 kWp potrzebuję falownika trójfazowego?',
      a: 'Często tak, zależnie od istniejącego przyłącza elektrycznego. Sprawdza to firma rzemieślnicza podczas przygotowywania oferty na podstawie rozdzielnicy.',
    },
  ],
  primaryCta: { label: 'Poproś teraz o bezpłatną ofertę', action: 'customer' },
  secondaryCta: {
    label: 'Koszt instalacji fotowoltaicznej: pełny przewodnik',
    action: 'link',
    href: '/koszt-instalacji-fotowoltaicznej',
  },
  relatedLinks: [
    { label: 'Instalacja fotowoltaiczna 10 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-10-kwp-cena' },
    { label: 'Instalacja fotowoltaiczna 15 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-15-kwp-cena' },
    { label: 'Instalacja fotowoltaiczna 20 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-20-kwp-cena' },
    { label: 'Magazyn energii 12 kWh: cena i wartości orientacyjne', href: '/magazyn-energii-12-kwh-cena' },
    { label: 'Zaplanuj instalację krok po kroku', href: '/zaplanuj-instalacje-fotowoltaiczna' },
  ],
}
